/** Sortable dashboard section: drag (mouse) / long-press drag (touch) or Arrange-mode arrows to reorder tiles; hide tiles you never use. Saved per user. */
import React, { useMemo, useState } from 'react';
import { DndContext, closestCenter, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, GripVertical, Move, RotateCcw } from 'lucide-react';
import { useTileFavourites } from './TileFavourites';
import { useSectionSorting } from './SortableSections';

export const applyOrder = (ids, order) => {
  if (!order?.length) return ids;
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...ids].sort((a, b) => (rank.has(a) ? rank.get(a) : 1e6 + ids.indexOf(a)) - (rank.has(b) ? rank.get(b) : 1e6 + ids.indexOf(b)));
};

function SortableTile({ id, arranging, index, count, onMove, hidden, onToggleHide, moveTo, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: hidden });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} className={`relative ${isDragging ? 'shadow-xl rounded-xl' : ''}`} data-testid={`sortable-${id}`} data-hidden={hidden ? 'true' : undefined} {...(arranging || hidden ? {} : { ...attributes, ...listeners })}>
      <div className={hidden ? 'opacity-40 grayscale pointer-events-none' : ''}>{children}</div>
      {hidden && (
        <button type="button" onClick={onToggleHide} className="absolute inset-0 rounded-xl border-2 border-dashed border-gray-300 bg-white/40 flex items-center justify-center gap-1.5 text-xs font-bold text-gray-700 hover:bg-white/70" data-testid={`unhide-${id}`}>
          <Eye className="w-4 h-4" /> Show this tile again
        </button>
      )}
      {arranging && !hidden && (
        <div className="absolute inset-0 rounded-xl bg-white/70 backdrop-blur-[1px] flex items-center justify-center gap-1 z-10" data-testid={`arrange-${id}`}>
          <button type="button" onClick={() => onMove(id, -1)} disabled={index === 0} aria-label="Move earlier" data-testid={`arrange-${id}-left`}
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-white border border-gray-300 text-gray-700 shadow-sm disabled:opacity-30 active:scale-95"><ChevronLeft className="w-4 h-4" /></button>
          <span className="h-9 px-2 inline-flex items-center rounded-lg bg-gray-900 text-white text-xs font-bold cursor-grab touch-none select-none" {...listeners} data-testid={`arrange-${id}-grip`}><GripVertical className="w-3.5 h-3.5 mr-1" /> {index + 1}</span>
          <button type="button" onClick={() => onMove(id, 1)} disabled={index === count - 1} aria-label="Move later" data-testid={`arrange-${id}-right`}
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-white border border-gray-300 text-gray-700 shadow-sm disabled:opacity-30 active:scale-95"><ChevronRight className="w-4 h-4" /></button>
          <button type="button" onClick={onToggleHide} aria-label="Hide tile" title="Hide this tile" data-testid={`hide-${id}`}
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg bg-white border border-rose-200 text-rose-600 shadow-sm active:scale-95 ml-1"><EyeOff className="w-4 h-4" /></button>
          {moveTo && (
            <select value="" onChange={(e) => e.target.value && moveTo.onMove(e.target.value)} title="Move this tile to another section (for everyone)" data-testid={`move-${id}`}
              className="h-9 max-w-[110px] text-[11px] font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 px-1 ml-1">
              <option value="">Move to…</option>
              {moveTo.sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

export function SortableSection({ sectionId, header, headerClass = '', children }) {
  const ctx = useTileFavourites();
  const sectionsCtx = useSectionSorting();
  const [localArranging, setArranging] = useState(false);
  const arranging = localArranging || !!ctx?.customising;
  const [revealed, setRevealed] = useState(false);
  const kids = React.Children.toArray(children).filter(Boolean);
  const idOf = (k, i) => k.props?.testId || `${sectionId}-${i}`;
  const baseIds = kids.map(idOf);
  const saved = ctx?.tileOrder?.[sectionId];
  const ids = useMemo(() => applyOrder(baseIds, saved), [baseIds.join('|'), (saved || []).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = Object.fromEntries(kids.map((k, i) => [idOf(k, i), k]));
  const customised = !!saved?.length && saved.join('|') !== baseIds.join('|');
  const hiddenSet = new Set(ctx?.hidden?.tiles || []);
  const hiddenHere = ids.filter((id) => hiddenSet.has(id));
  const visible = ids.filter((id) => !hiddenSet.has(id));
  const shown = arranging || revealed ? ids : visible;
  const collapsed = !!ctx?.collapsed?.includes(sectionId) && !arranging;
  const badgeTotal = visible.reduce((n, id) => n + (Number(byId[id]?.props?.badge) || 0), 0);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const commit = (next) => ctx?.setSectionOrder(sectionId, next);
  const crossDrag = !!sectionsCtx?.crossDrag;
  const { setNodeRef: dropRef, isOver } = useDroppable({ id: `drop-${sectionId}`, disabled: !crossDrag });
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    commit(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  const move = (id, dir) => {
    const i = visible.indexOf(id); const j = i + dir;
    if (i < 0 || j < 0 || j >= visible.length) return;
    const nextVisible = arrayMove(visible, i, j);
    commit([...nextVisible, ...hiddenHere]);
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${headerClass}`} data-testid={`${sectionId}-card`}>
      <div className={`flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 sm:py-2.5 ${collapsed ? '' : 'border-b border-black/5'}`}>
        {header}
      <div className="ml-auto flex items-center gap-1.5 sm:gap-2 text-[11px] shrink-0 whitespace-nowrap" data-testid={`${sectionId}-arrange-bar`}>
        {collapsed && (
          <span className="text-gray-500" data-testid={`${sectionId}-collapsed-summary`}>
            {visible.length}<span className="hidden sm:inline"> tile{visible.length === 1 ? '' : 's'}</span>{badgeTotal > 0 && <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold" data-testid={`${sectionId}-collapsed-badge`}>{badgeTotal > 99 ? '99+' : badgeTotal}</span>}
          </span>
        )}
        {hiddenHere.length > 0 && !arranging && !collapsed && (
          <button type="button" onClick={() => setRevealed((r) => !r)} className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900" data-testid={`${sectionId}-show-hidden`}>
            {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {revealed ? 'Hide again' : `Show hidden (${hiddenHere.length})`}
          </button>
        )}
        {arranging && customised && (
          <button type="button" onClick={() => { commit([]); }} className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900" data-testid={`${sectionId}-reset-order`}><RotateCcw className="w-3 h-3" /> Reset order</button>
        )}
        {ids.length > 1 && ctx && !collapsed && (
          <button type="button" onClick={() => { if (ctx.customising) ctx.setCustomising(false); else setArranging((a) => !a); }} data-testid={`${sectionId}-arrange-toggle`} aria-label={arranging ? 'Done arranging' : 'Arrange tiles'} title={arranging ? 'Done' : 'Arrange tiles in this section'}
            className={`h-7 inline-flex items-center gap-1 px-2 rounded-full border font-semibold ${arranging ? 'bg-gray-900 text-white border-gray-900' : 'bg-white/80 text-gray-500 border-gray-200 hover:text-gray-900'}`}>
            <Move className="w-3 h-3" /> <span className={arranging ? '' : 'hidden sm:inline'}>{arranging ? 'Done' : 'Arrange'}</span>
          </button>
        )}
        {ctx && (
          <button type="button" onClick={() => ctx.toggleCollapsed(sectionId)} aria-expanded={!collapsed} aria-label={collapsed ? 'Expand section' : 'Collapse section'} title={collapsed ? 'Expand' : 'Collapse to just the header'} data-testid={`${sectionId}-collapse-toggle`}
            className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-gray-200 bg-white/80 text-gray-500 hover:text-gray-900 hover:border-gray-400">
            <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
          </button>
        )}
      </div>
      </div>
      <div className={`bg-white/70 ${collapsed ? 'hidden' : 'p-2 sm:p-3'}`} data-testid={`${sectionId}-body`}>
      {visible.length === 0 && !arranging && !revealed && !collapsed && (
        <p className="w-full text-xs text-gray-400 italic" data-testid={`${sectionId}-all-hidden`}>All tiles in this section are hidden.</p>
      )}
      {(() => {
        const grid = (
          <div ref={dropRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full rounded-xl transition-colors ${collapsed ? 'hidden' : ''} ${crossDrag ? 'min-h-[64px] p-1 border-2 border-dashed ' + (isOver ? 'border-emerald-500 bg-emerald-50/60' : 'border-gray-200') : ''}`} aria-hidden={collapsed || undefined} data-testid={`${sectionId}-grid`} data-drop-over={isOver || undefined}>
            {shown.length === 0 && crossDrag && <p className="col-span-full text-xs text-gray-400 italic text-center py-3" data-testid={`${sectionId}-drop-empty`}>Drop tiles here</p>}
            {shown.map((id) => byId[id] && (
              <SortableTile key={id} id={id} arranging={arranging} index={visible.indexOf(id)} count={visible.length} onMove={move}
                hidden={hiddenSet.has(id)} onToggleHide={() => ctx?.toggleHidden('tiles', id)}
                moveTo={sectionsCtx?.isAdmin ? { sections: sectionsCtx.sections.filter((s) => s.id !== sectionId), onMove: (sec) => sectionsCtx.moveTile(id, sec) } : null}>{byId[id]}</SortableTile>
            ))}
          </div>
        );
        const sortable = <SortableContext items={shown} strategy={rectSortingStrategy}>{grid}</SortableContext>;
        return crossDrag ? sortable : <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>{sortable}</DndContext>;
      })()}
      {/* Hidden tiles stay mounted (invisible) so Favourites can still render them */}
      {ids.some((id) => hiddenSet.has(id) && !shown.includes(id)) && (
        <div className="hidden" aria-hidden="true">{ids.filter((id) => hiddenSet.has(id) && !shown.includes(id)).map((id) => <React.Fragment key={id}>{byId[id]}</React.Fragment>)}</div>
      )}
      </div>
    </div>
  );
}
